// Catálogo de "ganchos" (hooks) del modo "Video con influencer" — cada gancho
// trae un `prompt` (instrucción para el backend), un clip de preview vertical
// (`videoUrl`, 9:16) y su thumbnail. Se fetchea de la tabla `video_hooks`.
//
// Tabla `video_hooks` (ver migración 20260630120000_video_hooks.sql):
//   id, nombre, prompt, video_url, thumbnail_url, descripcion, activo, created_at
//
// Mientras no haya filas cargadas, el `GanchoPicker` cae a slots placeholder
// (9:16) para reservar/visualizar el espacio.

import { supabase } from '../../lib/supabase';

export interface GanchoOption {
  /** PK de la tabla `video_hooks`. */
  id: string;
  /** Nombre visible del gancho (columna `nombre`). */
  nombre: string;
  /** Instrucción del gancho que usa el backend para armar la apertura. */
  prompt: string;
  /** URL del clip vertical (9:16) de preview. Opcional mientras no haya asset. */
  videoUrl?: string;
  /** Poster/thumbnail del clip. Opcional. */
  posterUrl?: string;
  /** Texto corto opcional (explicador / subtítulo). */
  descripcion?: string;
}

/**
 * Cantidad de slots placeholder (9:16) que muestra el picker mientras no
 * haya ganchos cargados — solo para reservar/visualizar el espacio.
 */
export const GANCHO_PLACEHOLDER_SLOTS = 4;

/**
 * Id sintético de la opción "Sin gancho" (ADR-671). NO es una fila de
 * `video_hooks`: el picker la inyecta como una card más (la primera) y
 * cuando está elegida el payload viaja SIN la key `gancho_id`, así el
 * backend saltea la generación del clip de apertura y el concat.
 */
export const SIN_GANCHO_ID = 'sin-gancho';

/** ¿La selección actual es "Sin gancho"? (null = todavía no eligió nada). */
export const isSinGancho = (id: string | null | undefined): boolean =>
  id === SIN_GANCHO_ID;

// ─── Última elección de gancho (ADR-671) ──────────────────────────────────
// Igual que la voz (`VoiceGuionSection`), el modo recuerda con qué gancho
// generaste la última vez y lo pre-selecciona. Guarda el id CRUDO: un uuid de
// `video_hooks` o el sentinel `SIN_GANCHO_ID`. El restore valida contra las
// filas activas — si ese gancho se desactivó o se borró, no restaura nada y
// vuelve el bloqueo de ADR-551.
const LAST_GANCHO_KEY = 'atlas_v2_last_gancho';

/** Persiste la elección de gancho para la próxima vez. */
export function saveLastGancho(id: string | null): void {
  try {
    if (id) localStorage.setItem(LAST_GANCHO_KEY, id);
    else localStorage.removeItem(LAST_GANCHO_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Última elección VÁLIDA contra los ganchos activos recién fetcheados, o null
 * si no hay nada guardado / el gancho guardado ya no existe.
 */
export function getLastGancho(ganchos: GanchoOption[]): string | null {
  try {
    const raw = localStorage.getItem(LAST_GANCHO_KEY);
    if (!raw) return null;
    if (raw === SIN_GANCHO_ID) return SIN_GANCHO_ID;
    return ganchos.some((g) => g.id === raw) ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Devuelve los ganchos activos de `video_hooks`, ordenados por `created_at`.
 * Mapea las columnas snake_case al shape `GanchoOption` que usa la UI.
 */
export async function fetchVideoHooks(): Promise<GanchoOption[]> {
  const { data, error } = await supabase
    .from('video_hooks')
    .select('id, nombre, prompt, video_url, thumbnail_url, descripcion')
    .eq('activo', true)
    .order('created_at');
  if (error || !data) {
    console.error('[ganchosCatalog] fetchVideoHooks error', error);
    return [];
  }
  return (data as any[]).map(
    (g): GanchoOption => ({
      id: String(g.id),
      nombre: (g.nombre as string) || 'Gancho',
      prompt: (g.prompt as string) || '',
      videoUrl: (g.video_url as string) || undefined,
      posterUrl: (g.thumbnail_url as string) || undefined,
      descripcion: (g.descripcion as string) || undefined,
    }),
  );
}
